// Reine Hilfsfunktionen fuer den Stundenzettel - ohne React und ohne Importe,
// damit sie per Node testbar sind (__tests__/stundenzettelHilfen.node.test.cjs).
const zuMin = (t) => {
  const [h, m] = String(t || '').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
// Vollstaendig = beide Zeiten gesetzt und Ende nach Beginn. Tippfehler-Bloecke zaehlen nicht.
const vollstaendig = (b) => !!(b && b.start_time && b.end_time && zuMin(b.end_time) > zuMin(b.start_time));
// Pause als eigener Block: ein Block liegt ECHT innerhalb eines anderen (nicht am Rand).
// Typisch: Arbeitszeit 08:10-14:50 und zusaetzlich 12:00-12:30 als "Pause" eingetragen.
// Rueckgabe null oder { aussen, innen, vorschlag }. aussen/innen sind Indizes in der
// EINGABE-Reihenfolge - so arbeiten handleBlockChange und removeBlock.
export const findePauseAlsBlock = (blocks) => {
  const bl = blocks || [];
  for (let a = 0; a < bl.length; a++) {
    for (let i = 0; i < bl.length; i++) {
      if (a === i || !vollstaendig(bl[a]) || !vollstaendig(bl[i])) continue;
      const aussen = bl[a];
      const innen = bl[i];
      if (zuMin(aussen.start_time) < zuMin(innen.start_time) && zuMin(innen.end_time) < zuMin(aussen.end_time)) {
        return {
          aussen: a,
          innen: i,
          vorschlag: [
            { start_time: aussen.start_time, end_time: innen.start_time },
            { start_time: innen.end_time, end_time: aussen.end_time },
          ],
        };
      }
    }
  }
  return null;
};
// Teilt den aeusseren Block an der "Pause" und entfernt den Pausen-Block.
// Die Blockanzahl bleibt gleich (+1 Teilstueck, -1 Pausen-Block), MAX_BLOCKS kann also
// nicht ueberschritten werden. Die Stunden rechnet danach recalcEntry neu.
// Beseitigt NUR die Verschachtelung - andere Ueberschneidungen meldet danach die bisherige Pruefung.
export const pauseAlsLueckeAufteilen = (blocks) => {
  const treffer = findePauseAlsBlock(blocks);
  if (!treffer) return blocks;
  const { aussen, innen, vorschlag } = treffer;
  const rest = blocks.filter((_, k) => k !== aussen && k !== innen);
  const neu = [
    ...rest.filter(vollstaendig),
    { ...blocks[aussen], ...vorschlag[0] },
    { ...vorschlag[1], hours: 0 },
  ].sort((x, y) => zuMin(x.start_time) - zuMin(y.start_time));
  return [...neu, ...rest.filter((b) => !vollstaendig(b))];
};
// Stati, die "noch nichts erfasst" bedeuten bzw. vom System gesetzt werden.
const OHNE_ERFASSUNG = ['unbearbeitet', 'wochenende', 'ferien'];
// Tage fuer das Fenster "Auffällige Änderung erkannt".
// - Vorher gearbeitete Tage: geleert, auf Abwesenheit gestellt oder um mehr als 2 h geaendert.
// - Bewusst erfasste Abwesenheit (krank, Urlaub, Feiertag, ...): mit mehr als 2 h ueberschrieben.
// - Das erste Ausfuellen eines LEEREN Tages ist keine auffaellige Aenderung
//   (Rueckmeldung vom 11.09.2026 - das Fenster kam beim normalen Erfassen).
// Rueckgabe: ISO-Daten, die Formatierung macht die Seite.
export const auffaelligeTage = (original, entries, zaehltAlsArbeit) => {
  const nachDatum = {};
  for (const o of original || []) nachDatum[o.date] = o;
  const treffer = [];
  for (const e of entries || []) {
    const o = nachDatum[e.date];
    if (!o) continue;
    const warGearbeitet = (o.hours || 0) > 0;
    const warAbwesend = !warGearbeitet && !zaehltAlsArbeit(o.status)
      && !OHNE_ERFASSUNG.includes(o.status || 'unbearbeitet');
    if (!warGearbeitet && !warAbwesend) continue;
    const geloescht = warGearbeitet && ((e.hours || 0) <= 0 || !zaehltAlsArbeit(e.status));
    // In Hundertstel vergleichen - sonst gilt z. B. 4.03 -> 2.03 wegen Fliesskomma als "> 2".
    const starkGeaendert = Math.round(Math.abs((e.hours || 0) - (o.hours || 0)) * 100) > 200;
    if (geloescht || starkGeaendert) treffer.push(e.date);
  }
  return treffer;
};

// Grund "Sonstiges" braucht eine Bemerkung, aus der das Buero den Anlass erkennt:
// mindestens 10 Zeichen und 3 Buchstaben ("11" reicht nicht). Gibt die Daten TT.MM. zurueck.
export const sonstigesOhneKommentar = (liste) => {
  const fehlt = [];
  for (const s of liste || []) {
    if (!s || typeof s !== 'object') continue;
    if (String(s.grund || '').trim() !== 'Sonstiges') continue;
    const stunden = Number(s.hours);
    if (!(stunden > 0)) continue;
    const t = String(s.comment || '').trim();
    if ([...t].length >= 10 && (t.match(/\p{L}/gu) || []).length >= 3) continue;
    const d = String(s.date || '');
    fehlt.push([d, d.length >= 10 ? `${d.slice(8, 10)}.${d.slice(5, 7)}.` : d]);
  }
  fehlt.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  return fehlt.map((x) => x[1]);
};

// Die drei Stundenzahlen der erfassten Eintraege - gleiche Regel wie stundensummen im Backend.
// ERFASSTE Stunden: keine Wochendeckelung, kein Kontingent, keine Pauschale, keine Sonderstunden.
// Darf NIE werfen - sie laeuft im Unterschrifts-Dialog.
export const stundenSummen = (entries, zaehltAlsArbeit) => {
  let anwesend = 0;
  let kind_abwesend = 0;
  for (const e of (Array.isArray(entries) ? entries : [])) {
    if (!e || typeof e !== 'object') continue;
    const stunden = Number(e.hours) || 0;
    if (e.status === 'betreutes_kind_krank') kind_abwesend += stunden;
    else if (zaehltAlsArbeit && zaehltAlsArbeit(e.status)) anwesend += stunden;
  }
  const r = (x) => Math.round(x * 100) / 100;
  return { anwesend: r(anwesend), kind_abwesend: r(kind_abwesend), gesamt: r(anwesend + kind_abwesend) };
};

// Deutsches Format wie _de_num im Backend: 20 -> "20,00". Nie "NaN" anzeigen.
export const deZahl = (wert) => {
  const z = Number(wert);
  if (wert === null || wert === undefined || !Number.isFinite(z)) return '0,00';
  return z.toFixed(2).replace('.', ',');
};

// Gleiche Meldung wie im Backend (sonstiges_meldung).
export const sonstigesMeldung = (daten) => {
  const gezeigt = daten.slice(0, 5).join(', ');
  const rest = daten.length > 5 ? ` und ${daten.length - 5} weitere` : '';
  return `Bei Grund \u201eSonstiges\u201c bitte in der Bemerkung kurz beschreiben, wofür die Stunden sind (mindestens 10 Zeichen, davon mindestens 3 Buchstaben): ${gezeigt}${rest}`;
};
